import { RECORDS_PER_PAGE } from "@root/constants";
import {
  deleteNotice,
  doAddNotice,
  getNotices,
  updateNotice,
} from "@root/store/thunks";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  FETCH_NOTICES,
  DELETE_NOTICE,
  ADD_NOTICE,
  UPDATE_NOTICE,
  FETCH_PUBLISH_NOTICES,
} from "@root/store/action-types";
import { useNotification } from "./useNotification";
import { useTranslation } from "react-i18next";
import {
  getPublishNotices,
  pinNotice as pinNoticeThunk,
} from "@root/store/thunks/notice";
import { NoticeService } from "@root/services/notice";
import { useOrganizationBranding } from "@root/context/organization-branding-context";

function useNotice() {
  const dispatch = useDispatch();
  const { slug } = useOrganizationBranding();
  const { showNotification } = useNotification();
  const { t } = useTranslation("common");
  const noticeService = new NoticeService();
  const [noticeLoading, setNoticeloading] = useState(false);
  const [addNoticeLoading, setAddNoticeLoading] = useState(false);
  const [deleteNoticeLoading, setDeleteNoticeLoading] = useState(false);
  const [updateNoticeLoading, setUpdateNoticeLoading] = useState(false);
  const [downloadingNotices, setDownloadingNotices] = useState<Set<number>>(
    new Set(),
  );

  const [noticeData, setNoticeData] = useState<any>(null);
  const [publishNoticeData, setPublishNoticeData] = useState<any>(null);
  const [publishNoticeLoading, setPublishNoticeLoading] = useState(false);
  const [noticeCount, setNoticeCount] = useState(0);
  const [noticePage, setNoticePage] = useState(1);

  const [pageNo, setPageNo] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [publishNoticePageNo, setPublishNoticePageNo] = useState(1);
  const [publishNoticePageCount, setPublishNoticePageCount] = useState(0);
  const pageSize = RECORDS_PER_PAGE;

  const onSuccess = (data: any, identifier: any, callaback: any) => {
    if (identifier === FETCH_NOTICES) {
      setNoticeloading(false);
      // Handle both old format (array) and new format (object with rows and count)
      const rows = Array.isArray(data)
        ? data
        : data?.rows || data?.data?.rows || data;
      const count =
        data?.count || data?.data?.count || data?.data?.totalCount || 0;
      setNoticeData(rows);
      setPublishNoticeData(rows);
      setPageCount(count);
    }
    if (identifier === FETCH_PUBLISH_NOTICES) {
      setPublishNoticeLoading(false);
      // Handle both old format (array) and new format (object with rows and count)
      const rows = Array.isArray(data)
        ? data
        : data?.rows || data?.data?.rows || data;
      const count =
        data?.count || data?.data?.count || data?.data?.totalCount || 0;
      setPublishNoticeData(rows);
      setPublishNoticePageCount(count);
    }
    if (identifier === DELETE_NOTICE) {
      setDeleteNoticeLoading(false);
      showNotification({
        type: "warning",
        message: t("notice.deleteSuccess"),
      });
      fetchNotice();
    }
    if (identifier === ADD_NOTICE) {
      setAddNoticeLoading(false);
      // Show success notification for add
      showNotification({
        type: "success",
        message: t("notice.createSuccess"),
      });
      // Refresh the data after adding
      fetchNotice();
    }
    if (identifier === UPDATE_NOTICE) {
      setUpdateNoticeLoading(false);
      // Show success notification for update
      showNotification({
        type: "success",
        message: t("notice.updateSuccess"),
      });
      // Refresh the data after updating
      fetchNotice();
    }
  };

  const onError = (data: any, identifier: any) => {
    if (identifier === FETCH_NOTICES) {
      setNoticeloading(false);
      showNotification({
        type: "error",
        message: t("notice.fetchError"),
      });
    }
    if (identifier === DELETE_NOTICE) {
      setDeleteNoticeLoading(false);
      showNotification({
        type: "error",
        message: t("notice.deleteError"),
      });
    }
    if (identifier === ADD_NOTICE) {
      setAddNoticeLoading(false);
      showNotification({
        type: "error",
        message: t("notice.createError"),
      });
    }
    if (identifier === UPDATE_NOTICE) {
      setUpdateNoticeLoading(false);
      showNotification({
        type: "error",
        message: t("notice.updateError"),
      });
    }
  };

  const addNotice = (payload: any) => {
    setAddNoticeLoading(true);
    console.log("Dispatching addNotice with payload:", payload);
    dispatch(
      doAddNotice({
        payload,
        onSuccess,
        onError,
      }),
    );
  };

  const fetchNotice = (search = "") => {
    setNoticeloading(true);
    const limit = pageSize;
    const offset = (pageNo - 1) * pageSize;
    dispatch(
      getNotices({
        onSuccess,
        onError,
        search,
        limit,
        offset,
        slug: slug ?? "",
      }),
    );
  };

  const fetchPublishNotice = (
    search = "",
    page?: number,
    noticeTypeId?: any,
    slugOverride?: string,
  ) => {
    // Prefer the explicitly-passed slug (avoids stale-closure issues on fast load),
    // then fall back to the slug captured in this hook's render cycle.
    const effectiveSlug = slugOverride ?? slug ?? "";
    if (!effectiveSlug) return; // Don't call the API until slug is available
    setPublishNoticeLoading(true);
    const currentPage = page !== undefined ? page : publishNoticePageNo;
    const limit = pageSize;
    const offset = (currentPage - 1) * pageSize;
    const finalNoticeTypeId = noticeTypeId ?? "";
    dispatch(
      getPublishNotices({
        onSuccess,
        onError,
        search,
        limit,
        offset,
        noticeTypeId: finalNoticeTypeId,
        slug: effectiveSlug,
      }),
    );
  };

  const removeNotice = (id: number) => {
    setDeleteNoticeLoading(true);
    dispatch(
      deleteNotice({
        id,
        onSuccess,
        onError,
      }),
    );
  };

  const doUpdateNotice = (payload: any, id: number) => {
    setUpdateNoticeLoading(true);
    dispatch(
      updateNotice({
        payload,
        id,
        onError,
        onSuccess,
      }),
    );
  };

  const pinNotice = (id: number, pin: boolean) => {
    dispatch(
      pinNoticeThunk({
        id,
        pin,
        onSuccess: () => {
          showNotification({
            type: "success",
            message: t("notice.pinSuccess"),
          });
          fetchNotice();
        },
        onError: () => {
          showNotification({ type: "error", message: t("notice.pinError") });
        },
      }),
    );
  };

  const downloadDocumentsZip = async (
    documentIds: number[],
    noticeId?: number,
    fileName = "notices.zip",
  ) => {
    if (!documentIds || documentIds.length === 0) return;

    setDownloadingNotices((prev) => {
      const newSet = new Set(prev);
      if (noticeId) {
        newSet.add(noticeId);
      }
      return newSet;
    });

    try {
      const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
      const response = await noticeService.downloadDocumentsZip(
        baseAuthUrl,
        documentIds,
        noticeId,
      );

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading ZIP:", error);
      showNotification({
        type: "error",
        message: t("notice.downloadError"),
      });
    } finally {
      setDownloadingNotices((prev) => {
        const newSet = new Set(prev);
        if (noticeId) {
          newSet.delete(noticeId);
        }
        return newSet;
      });
    }
  };

  const isDownloadingNotice = (noticeId?: number) => {
    if (!noticeId) return false;
    return downloadingNotices.has(noticeId);
  };

  return {
    noticeLoading,
    addNoticeLoading,
    deleteNoticeLoading,
    updateNoticeLoading,
    noticeData,
    pageNo,
    setPageNo,
    pageCount,
    setPageCount,
    doUpdateNotice,
    removeNotice,
    fetchNotice,
    addNotice,
    pinNotice,
    publishNoticeData,
    publishNoticeLoading,
    fetchPublishNotice,
    publishNoticePageNo,
    setPublishNoticePageNo,
    publishNoticePageCount,
    noticeCount,
    noticePage,
    setNoticePage,
    downloadDocumentsZip,
    isDownloadingNotice,
  };
}
export default useNotice;
